import React from "react";
import { withRouter } from "react-router-dom";
import { Formik, Form } from "formik";
import { CreateResellerSchema } from "../../forms/schema";
import { TextInput } from "../../forms/components";
import withSSR from "../../components/withSSR";
import DataTable from "../../components/DataTable";
import ManageUserModal from "./ManageUserModal";
import ManageRateModal from "./ManageRateModal";

import {
	getAccessControl,
	getResellers,
	createReseller,
	getResellerUsers,
	getBaseRate,
	getGateway,
	getCommissionType,
	getResellerRateUpdates,
} from "../../libs/functions";

import Container from "../../components/Container";
import Util from "../../libs/util";

import Swal from "sweetalert2";

class Resellers extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			resellers: [],
			users: [],
			targetReseller: undefined,
			targetUser: undefined,
			targetMDR: {},
			targetRateUpdates: {},
			gateway: {},
			master: false,
		};
	}

	componentDidMount = async () => {
		const accessControl = await getAccessControl();
		this.setState({
			master: accessControl === "master",
			gateway: await getGateway(),
		});
		if (accessControl !== "master") {
			this.props.history.push("/merchants");
		}

		this.retrieveReseller();
	};

	retrieveReseller = () => {
		this.setState({ resellers: [] });
		this.props.fetchData(this.container.loader, getResellers).then((data) => {
			if (!this.state.master)
				data = data.filter((i) => i.accessControlType !== "master");
			else
				data = [
					data.find((i) => i.accessControlType === "master"),
					...data.filter((i) => i.accessControlType !== "master"),
				];

			this.setState({ resellers: data });
		});
	};

	handleSubmit = (values, { setSubmitting, resetForm }) => {
		Swal.fire({
			title: "Creating Reseller",
			onBeforeOpen: () => {
				Util.hideModal("create-reseller").then(() => {
					resetForm();
				});
				Swal.showLoading();
			},
			onOpen: async () => {
				try {
					let status = await createReseller({ ...values });
					if (status) {
						setSubmitting(false);
						Swal.fire({
							icon: "success",
							title: "Reseller Created",
							onClose: () => {
								this.retrieveReseller();
							},
						});
					}
				} catch (err) {
					setSubmitting(false);
					if (err.response) {
						if (err.response.status === 401) {
							Util.expireSession().then(() => {
								window.location = "/login";
							});
						} else {
							Swal.fire({
								icon: "error",
								title: `Unable to Create reseller`,
							});
						}
					}
				}
			},
		});
	};

	retrieveUsers = async (reseller) => {
		this.setState({ users: [], targetReseller: undefined }, async () => {
			const users = await getResellerUsers(reseller._id);
			this.setState({ users, targetReseller: reseller });
		});
	};

	manageUser = (reseller) => {
		this.retrieveUsers(reseller).then(async () => {
			await Util.showModal("manage-users");
		});
	};

	manageRates = (reseller) => {
		const { _id, name, accessControlType } = reseller;
		Swal.fire({
			title: `Retrieving ${name}'s Rates`,
			onBeforeOpen: () => {
				Swal.showLoading();
			},
			onOpen: async () => {
				try {
					const mdr =
						accessControlType !== "master"
							? await getBaseRate(_id)
							: await getBaseRate();
					delete mdr.stripe;
					const commissionType = await getCommissionType(_id); //Get latest commission type

					const { rateUpdates } = await getResellerRateUpdates(_id);

					this.setState(
						{
							targetMDR: { reseller: { ...reseller, commissionType }, mdr },
							targetRateUpdates: {
								scheduled: rateUpdates.filter(
									(i) => i.completedAt === undefined
								),
								completed: rateUpdates.filter(
									(i) => i.completedAt !== undefined
								),
							},
						},
						async () => {
							Swal.close();
							await Util.showModal("manage-rates");
						}
					);
				} catch (err) {
					if (err.response) {
						if (err.response.status === 401) {
							Util.expireSession().then(() => {
								window.location = "/login";
							});
						} else {
							Swal.fire({
								icon: "error",
								title: `Unable to retrieve ${name}'s rates`,
							});
						}
					}
				}
			},
		});
	};

	refreshRates = async () => {
		const {
			reseller: { _id, accessControlType },
		} = this.state.targetMDR;

		const mdr =
			accessControlType !== "master"
				? await getBaseRate(_id)
				: await getBaseRate();
		delete mdr.stripe;
		const commissionType = await getCommissionType(_id);
		const { rateUpdates } = await getResellerRateUpdates(_id);

		this.setState({
			targetMDR: {
				reseller: {
					...this.state.targetMDR.reseller,
					commissionType,
				},
				mdr,
			},
			targetRateUpdates: {
				scheduled: rateUpdates.filter((i) => i.completedAt === undefined),
				completed: rateUpdates.filter((i) => i.completedAt !== undefined),
			},
		});
	};

	render() {
		const { identifier, MDRTypes } = this.props;
		const {
			resellers,
			users,
			targetReseller,
			targetMDR,
			targetRateUpdates,
			gateway,
		} = this.state;
		return (
			<Container ref={(c) => (this.container = c)} activeMenu={identifier}>
				<div className="Merchants">
					<div className="uk-flex uk-flex-row uk-flex-middle uk-margin-bottom">
						<h1 className="content-header uk-margin-remove-bottom">
							{identifier}
						</h1>
						<button
							className="uk-margin-left uk-icon-button uk-button-primary"
							uk-icon="icon: fa-solid-plus-circle; ratio: 0.7"
							uk-toggle="target: #create-reseller"
							type="button"
						/>
					</div>
					{resellers.length > 0 && (
						<div className="uk-card uk-card-small uk-overflow-auto uk-card-body uk-card-default">
							<DataTable
								ref={this.datatableRef}
								className="uk-table uk-table-hover uk-table-striped uk-table-middle uk-table-responsive sales-channel-table"
							>
								<thead>
									<tr>
										<th className="uk-table-shrink">No.</th>
										<th className="uk-width-1-3">Name</th>
										{/* <th className="uk-width-1-6">Commission</th> */}
										<th className="uk-text-center uk-width-1-5">Rates</th>
										<th className="uk-text-center uk-width-1-5">Users</th>
										<th className="uk-text-center uk-width-1-5">Created At</th>
									</tr>
								</thead>
								<tbody>
									{resellers.map((reseller, index) => {
										return (
											<tr key={index}>
												<td>
													{reseller.accessControlType === "master" ? (
														<i
															className="uk-margin-small-right form-header-icon"
															uk-icon="icon: fa-solid-star; ratio:0.8"
														/>
													) : (
														<span>{index}</span>
													)}
												</td>
												<td>
													<span>{reseller.name}</span>
												</td>
												{/* <td>
													<span>
														{reseller.percentOfTotalComm
															? `${reseller.percentOfTotalComm}%`
															: "-"}
													</span>
												</td> */}
												<td>
													<button
														className="uk-button uk-button-small uk-button-primary uk-width-1-1 action-btn"
														type="button"
														onClick={() => this.manageRates(reseller)}
													>
														<span className="uk-text-middle uk-text-bold">
															Manage
														</span>
													</button>
												</td>
												<td className="uk-text-center uk-text-break">
													<button
														className="uk-button uk-button-small uk-button-primary uk-width-1-1 action-btn"
														type="button"
														onClick={() => this.manageUser(reseller)}
													>
														<span className="uk-text-middle uk-text-bold">
															Manage
														</span>
													</button>
												</td>
												<td className="uk-text-center">
													{Util.toDateTimeString(reseller.createdAt)}
												</td>
											</tr>
										);
									})}
								</tbody>
							</DataTable>
						</div>
					)}
				</div>
				<div id="create-reseller" uk-modal="bg-close: false">
					<div className="uk-modal-dialog uk-modal-body">
						<h2 className="uk-modal-title">Create Reseller</h2>
						<Formik
							initialValues={CreateResellerSchema.initialValues}
							validationSchema={CreateResellerSchema.validation}
							onSubmit={this.handleSubmit}
						>
							{(props) => (
								<Form className="uk-form-stacked" onSubmit={props.handleSubmit}>
									<TextInput
										label="Name"
										id="name"
										name="name"
										type="text"
										placeholder=""
									/>
									<p className="uk-text-right">
										<button
											className="uk-button uk-button-default"
											type="button"
											onClick={() => {
												props.resetForm();
												Util.hideModal("create-reseller");
											}}
										>
											Cancel
										</button>
										<button
											className="uk-button uk-button-primary"
											type="submit"
										>
											Create
										</button>
									</p>
								</Form>
							)}
						</Formik>
					</div>
				</div>
				<ManageUserModal
					reseller={targetReseller}
					users={users}
					retrieveUsers={this.retrieveUsers}
				/>
				<ManageRateModal
					details={targetMDR}
					targetRateUpdates={targetRateUpdates}
					gateway={gateway}
					MDRTypes={MDRTypes}
					refreshRates={this.refreshRates}
				/>
			</Container>
		);
	}
}

export default withSSR(withRouter(Resellers));
